import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Heart, Loader2 } from "lucide-react";
import { SHOPIFY_CONFIGURED, fetchAllProducts } from "@/lib/shopify";
import ProductCard from "@/components/ProductCard";

/**
 * Best Sellers: products tagged "best-seller" (or "bestseller") in Shopify,
 * with a graceful fallback to the most recent products if no items are tagged.
 */
export default function BestSellers() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        if (!SHOPIFY_CONFIGURED) {
            setLoading(false);
            return;
        }
        fetchAllProducts(40)
            .then((all) => {
                if (cancelled) return;
                const tagged = all.filter((p) =>
                    p.tags.some((t) =>
                        ["best-seller", "bestseller", "best seller", "featured"].includes(
                            t.toLowerCase()
                        )
                    )
                );
                setProducts(tagged.length ? tagged : all.slice(0, 12));
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err.message || "Could not load products");
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div data-testid="best-sellers-page" className="cream-textured">
            <section className="relative py-16 md:py-24 text-center sparkle-overlay">
                <div className="relative z-10 max-w-3xl mx-auto px-6">
                    <p className="font-script text-3xl text-bb-pink-script">Customer Favorites</p>
                    <h1 className="font-serif-display text-4xl md:text-6xl tracking-[0.1em] uppercase text-bb-text">
                        Best Sellers
                    </h1>
                    <div className="mt-3 flex items-center justify-center gap-3 text-bb-rose-gold">
                        <span className="swoosh w-14" />
                        <Star size={14} className="fill-current text-bb-pink-script" />
                        <span className="swoosh w-14" />
                    </div>
                    <p className="mt-5 text-bb-text-soft">
                        The pieces our boutique community loves the most — tried, loved, and worn proudly.
                    </p>
                </div>
            </section>

            <section className="py-10 md:py-14 relative overflow-hidden">
                <div className="leopard-blob leopard-blob--left" aria-hidden="true" />
                <div className="leopard-blob leopard-blob--right" aria-hidden="true" />
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
                    {SHOPIFY_CONFIGURED && loading && (
                        <div className="flex flex-col items-center justify-center py-20 text-bb-text-soft">
                            <Loader2 size={28} className="animate-spin text-bb-pink-script" />
                            <p className="mt-3 text-sm tracking-[0.2em] uppercase">Loading favorites</p>
                        </div>
                    )}
                    {SHOPIFY_CONFIGURED && !loading && error && (
                        <p className="text-center text-bb-text-soft py-12">{error}</p>
                    )}
                    {SHOPIFY_CONFIGURED && !loading && !error && products.length === 0 && (
                        <p className="text-center text-bb-text-soft py-12">
                            Best sellers coming soon — check back shortly.
                        </p>
                    )}
                    {SHOPIFY_CONFIGURED && !loading && products.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
                            {products.map((p) => (
                                <ProductCard key={p.id} product={p} testIdPrefix="best-seller" />
                            ))}
                        </div>
                    )}
                </div>
                <div className="mt-12 text-center relative">
                    <Link to="/shop" className="btn-black btn-shine" data-testid="best-sellers-cta">
                        Shop All <Heart size={14} className="fill-current text-bb-pink-script" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
